import React, { useState } from 'react';
import ContactFormModal from '../00-shared/ContactFormModal';

interface PricingPlansSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

const highlights = [
    {
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
        ),
        title: 'Tailored to Your Program',
        description: 'Every team is different. We build a plan around your roster size, devices, and coaching workflow.',
    },
    {
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        title: '30-Minute Walkthrough',
        description: 'See real dashboards, real data flows, and real insights — no slides, no fluff.',
    },
    {
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        ),
        title: 'No Commitment',
        description: 'Explore the platform risk-free. We\'ll answer every question before you decide.',
    },
];

const PricingPlansSection: React.FC<PricingPlansSectionProps> = ({ highlightColor }) => {
    const [showDemoModal, setShowDemoModal] = useState(false);

    return (
        <>
            <section className="pt-32 pb-36 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white/10 to-transparent pointer-events-none"></div>
                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Header */}
                    <div className="max-w-7xl mx-auto pb-12 border-b border-neutral-800 relative z-10 text-center">
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 observe-fade opacity-0">
                            Book a Demo
                        </h1>
                        <p className="text-gray-300 observe-fade opacity-0 max-w-2xl mx-auto">
                            See how CGM Sports can transform your team's performance with AI-powered insights,
                            real-time monitoring, and dedicated support.
                        </p>
                    </div>

                    {/* Highlight cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
                        {highlights.map((item, index) => (
                            <div
                                key={index}
                                className="bg-neutral-900 p-8 observe-fade opacity-0 transition-all duration-300 hover:shadow-xl flex flex-col items-center text-center grid-accent"
                            >
                                <div className="text-white mb-4">{item.icon}</div>
                                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>

                    {/* CTA block */}
                    <div className="mt-16 observe-fade opacity-0">
                        <div className="bg-neutral-900/50 border border-neutral-800 p-10 text-center transition-all duration-300 hover:border-neutral-600 hover:bg-neutral-900">
                            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Ready to See It in Action?
                            </h3>
                            <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                                Schedule a personalized walkthrough with our team. We'll show you how CGM Sports
                                fits your roster, your devices, and your coaching style — with custom plans for
                                sports teams, PT clinics, gyms, and trainers.
                            </p>
                            <button
                                onClick={() => setShowDemoModal(true)}
                                className="px-8 py-4 text-sm font-semibold transition-all duration-200 ease-out hover:scale-105 cursor-pointer"
                                style={{
                                    backgroundColor: 'transparent',
                                    color: 'white',
                                    border: '1px solid white',
                                    borderRadius: '0',
                                    boxShadow: 'none'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                    e.currentTarget.style.color = 'black';
                                    e.currentTarget.style.boxShadow = '0 2px 10px -1px rgba(0, 0, 0, 0.1), 0 2px 8px -1px rgba(0, 0, 0, 0.06)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                BOOK A DEMO
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <ContactFormModal
                isOpen={showDemoModal}
                onClose={() => setShowDemoModal(false)}
                highlightColor={highlightColor}
            />
        </>
    );
};

export default PricingPlansSection;

/* ==========================================================================
 * ORIGINAL PRICING PLANS IMPLEMENTATION (commented out for future use)
 * ==========================================================================
 *
 * interface Plan {
 *     name: string;
 *     price?: number | string;
 *     priceLabel?: string;
 *     customPriceText?: string;
 *     description: string;
 *     features: string[];
 *     buttonText?: string;
 * }
 *
 * interface CustomerType {
 *     id: string;
 *     label: string;
 *     plans: Plan[];
 * }
 *
 * const OriginalPricingPlansSection: React.FC<PricingPlansSectionProps> = ({ highlightColor }) => {
 *     const [selectedType, setSelectedType] = useState(0);
 *
 *     const customerTypes: CustomerType[] = [
 *         {
 *             id: 'sports-teams',
 *             label: 'Sports Teams',
 *             plans: [
 *                 {
 *                     name: 'Core',
 *                     price: 'Free',
 *                     description: 'For small teams starting to unify athlete data.',
 *                     features: [
 *                         'Up to 10 athletes',
 *                         'Connect basic wearables (WHOOP, Garmin, Oura)',
 *                         'Simple readiness and recovery trends',
 *                         'Standard team dashboard',
 *                         'Email support'
 *                     ],
 *                     buttonText: 'Start Core Plan'
 *                 },
 *                 {
 *                     name: 'Performance',
 *                     price: 500,
 *                     priceLabel: '/month',
 *                     description: 'For full rosters and performance staffs.',
 *                     features: [
 *                         'Up to 60 athletes',
 *                         'Automated reports & readiness alerts',
 *                         'Team-specific insight models',
 *                         'Multi-sport dashboards',
 *                         'Priority support',
 *                         'Data cleaning & quality filters',
 *                     ],
 *                     buttonText: 'Start Performance Plan'
 *                 },
 *                 {
 *                     name: 'Pro',
 *                     price: 'Contact for pricing',
 *                     description: 'For programs requiring custom integration & scaling.',
 *                     features: [
 *                         'Unlimited athletes',
 *                         'Custom model tuned to your training system',
 *                         'Dedicated onboarding & white-glove support',
 *                         'API and testing data integrations',
 *                         'Administrative controls',
 *                         'Compliance-ready environment',
 *                     ],
 *                     buttonText: 'Contact Sales'
 *                 }
 *             ]
 *         },
 *         {
 *             id: 'pt-clinics',
 *             label: 'PT Clinics',
 *             plans: [
 *                 {
 *                     name: 'Core',
 *                     price: 'Free',
 *                     description: 'For clinics wanting simple client data visibility.',
 *                     features: [
 *                         'Up to 5 active clients',
 *                         'Connect wearables across clients',
 *                         'Basic readiness/recovery view',
 *                         'Progress snapshots',
 *                         'Email support'
 *                     ],
 *                     buttonText: 'Start Core Plan'
 *                 },
 *                 {
 *                     name: 'Performance',
 *                     price: 150,
 *                     priceLabel: '/month',
 *                     description: 'For clinics offering hybrid or long-term client monitoring.',
 *                     features: [
 *                         'Up to 50 clients',
 *                         'Automated check-ins & weekly summaries',
 *                         'Trend monitoring (load, adherence, recovery)',
 *                         'Client-specific insight models',
 *                         'Priority support',
 *                         'Enhanced data cleaning',
 *                     ],
 *                     buttonText: 'Start Performance Plan'
 *                 },
 *                 {
 *                     name: 'Pro',
 *                     price: 'Contact for pricing',
 *                     description: 'For clinics delivering high-end rehab & performance care.',
 *                     features: [
 *                         'Unlimited clients',
 *                         'Custom insight model tuned to your clinical method',
 *                         'Dedicated onboarding for staff',
 *                         'Scheduling + notes system integrations',
 *                         'Multi-clinician collaboration',
 *                         'White-glove support',
 *                     ],
 *                     buttonText: 'Contact Sales'
 *                 }
 *             ]
 *         },
 *         {
 *             id: 'gyms-trainers',
 *             label: 'Gyms & Trainers',
 *             plans: [
 *                 {
 *                     name: 'Core',
 *                     price: 'Free',
 *                     description: 'For coaches training a small client roster.',
 *                     features: [
 *                         'Up to 3 clients',
 *                         'Wearable aggregation',
 *                         'Basic readiness/recovery snapshots',
 *                         'Single-coach dashboard',
 *                         'Email support',
 *                     ],
 *                     buttonText: 'Start Core Plan'
 *                 },
 *                 {
 *                     name: 'Performance',
 *                     price: 100,
 *                     priceLabel: '/month',
 *                     description: 'For serious coaches scaling their client base.',
 *                     features: [
 *                         'Up to 30 clients',
 *                         'Weekly automated reports',
 *                         'Client-specific training insights',
 *                         'Progress tracking dashboards',
 *                         'Priority support',
 *                     ],
 *                     buttonText: 'Start Performance Plan'
 *                 },
 *                 {
 *                     name: 'Pro',
 *                     price: 'Contact for pricing',
 *                     description: 'For performance gyms and multi-coach facilities.',
 *                     features: [
 *                         'Unlimited clients',
 *                         'Custom insights aligned to your training system',
 *                         'Multi-coach collaboration & admin controls',
 *                         'Dedicated onboarding',
 *                         'White-glove facility support',
 *                     ],
 *                     buttonText: 'Contact Sales'
 *                 }
 *             ]
 *         }
 *     ];
 *
 *     const currentPlans = customerTypes[selectedType].plans;
 *
 *     return (
 *         <section className="pt-32 pb-36 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
 *             <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
 *             <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white/10 to-transparent pointer-events-none"></div>
 *             <div className="max-w-7xl mx-auto relative z-10">
 *                 <div className="max-w-7xl mx-auto pb-12 border-b border-neutral-800 relative z-10 text-center">
 *                     <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 observe-fade opacity-0">
 *                         Pricing
 *                     </h1>
 *                     <p className="text-gray-300 observe-fade opacity-0">
 *                         Flexible pricing plans designed for teams of all sizes. All plans include AI-powered insights, real-time monitoring, and dedicated support.
 *                     </p>
 *                 </div>
 *                 <div className="text-center mb-12 observe-fade opacity-0 mt-12">
 *                     <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Choose your plan</h2>
 *
 *                     <div className="flex items-center justify-center">
 *                         <div
 *                             className="relative inline-flex h-12 items-center gap-0 p-1 transition-all duration-300 border border-neutral-700"
 *                             style={{
 *                                 backgroundColor: 'rgb(38, 38, 38)',
 *                             }}
 *                         >
 *                             {customerTypes.map((type, index) => (
 *                                 <button
 *                                     key={type.id}
 *                                     onClick={() => setSelectedType(index)}
 *                                     className="relative flex-1 px-4 md:px-6 py-2 focus:outline-none transition-all duration-200"
 *                                     aria-label={`Select ${type.label}`}
 *                                 >
 *                                     <span
 *                                         className={`absolute inset-0 h-full transition-all duration-300 ease-in-out ${
 *                                             selectedType === index ? 'opacity-0' : 'opacity-0 hover:opacity-20'
 *                                         }`}
 *                                         style={{ backgroundColor: 'white' }}
 *                                     />
 *                                     <span
 *                                         className={`absolute inset-0 h-full transition-all duration-300 ease-in-out ${
 *                                             selectedType === index ? 'opacity-100' : 'opacity-0'
 *                                         }`}
 *                                         style={{ backgroundColor: 'white' }}
 *                                     />
 *                                     <span
 *                                         className={`relative z-10 text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
 *                                             selectedType === index ? 'text-black' : 'text-gray-400 hover:text-gray-200'
 *                                         }`}
 *                                     >
 *                                         {type.label}
 *                                     </span>
 *                                 </button>
 *                             ))}
 *                         </div>
 *                     </div>
 *                 </div>
 *
 *                 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
 *                     {currentPlans.map((plan, index) => (
 *                         <div
 *                             key={index}
 *                             className="bg-neutral-900 p-8 observe-fade opacity-0 transition-all duration-300 hover:shadow-xl flex flex-col grid-accent"
 *                         >
 *                             <h3 className="text-2xl font-bold text-white mb-4">{plan.name}</h3>
 *                             <div className="mb-4">
 *                                 {plan.customPriceText ? (
 *                                     <span className="text-2xl font-bold text-white">
 *                                         {plan.customPriceText}
 *                                     </span>
 *                                 ) : (
 *                                     <>
 *                                         <span className="text-4xl font-bold text-white">
 *                                             {typeof plan.price === 'number' ? `$${plan.price.toLocaleString()}` : plan.price}
 *                                         </span>
 *                                         {plan.priceLabel && <span className="text-gray-400">{plan.priceLabel}</span>}
 *                                     </>
 *                                 )}
 *                             </div>
 *                             <p className="text-gray-300 mb-4 min-h-[3rem]">{plan.description}</p>
 *                             <ul className="space-y-3 mb-8 flex-grow">
 *                                 {plan.features.map((feature, idx) => (
 *                                     <li key={idx} className="flex items-start text-gray-300">
 *                                         <svg
 *                                             className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5"
 *                                             fill="none"
 *                                             stroke="currentColor"
 *                                             viewBox="0 0 24 24"
 *                                             style={{ color: highlightColor.primary }}
 *                                         >
 *                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
 *                                         </svg>
 *                                         <span>{feature}</span>
 *                                     </li>
 *                                 ))}
 *                             </ul>
 *                         </div>
 *                     ))}
 *                 </div>
 *             </div>
 *         </section>
 *     );
 * };
 *
 * ========================================================================== */
